/**
 * ================================================================
 * REGISTRATION STORAGE UTILITY
 * ================================================================
 *
 * Helper centralized để quản lý dữ liệu đăng ký tạm thời trong sessionStorage.
 * Giúp tránh việc sử dụng key hardcoded rải rác và logic lặp lại.
 */

const REGISTRATION_DATA_KEY = "mebi_registration_data";

interface RegistrationData {
  phone: string;
  email: string;
  password?: string;
  purpose: "register" | "forgot-password" | "reset_password";
  fullName?: string;
  timestamp: number;
}

export const registrationStorage = {
  /**
   * Lưu số điện thoại (bước đầu tiên của đăng ký)
   */
  savePhone: (phone: string) => {
    try {
      const existing = registrationStorage.get();
      const payload: RegistrationData = {
        phone,
        email: existing?.email || "",
        purpose: existing?.purpose || "register",
        timestamp: Date.now(),
        ...existing,
      };
      sessionStorage.setItem(REGISTRATION_DATA_KEY, JSON.stringify(payload));
    } catch (error) {
      console.error("Failed to save phone:", error);
    }
  },

  /**
   * Lưu dữ liệu đăng ký
   */
  save: (data: Omit<RegistrationData, "timestamp">) => {
    try {
      const payload: RegistrationData = {
        ...data,
        timestamp: Date.now(),
      };
      sessionStorage.setItem(REGISTRATION_DATA_KEY, JSON.stringify(payload));

      // Legacy fallback (để tương thích ngược nếu cần, nhưng nên xóa dần)
      // sessionStorage.setItem('registrationData', JSON.stringify(payload));
    } catch (error) {
      console.error("Failed to save registration data:", error);
    }
  },

  /**
   * Lấy dữ liệu đăng ký
   */
  get: (): RegistrationData | null => {
    try {
      // Ưu tiên key mới
      const data = sessionStorage.getItem(REGISTRATION_DATA_KEY);
      if (data) return JSON.parse(data);

      // Fallback kiểm tra các key cũ (legacy cleanup)
      const legacyKeys = [
        "registrationData",
        "tempRegistrationData",
        "pendingRegistration",
      ];
      for (const key of legacyKeys) {
        const legacyData = sessionStorage.getItem(key);
        if (legacyData) {
          // Migrate sang key mới
          const parsed = JSON.parse(legacyData);
          sessionStorage.setItem(REGISTRATION_DATA_KEY, JSON.stringify(parsed));
          return parsed;
        }
      }
      return null;
    } catch (error) {
      console.error("Failed to get registration data:", error);
      return null;
    }
  },

  /**
   * Xóa dữ liệu đăng ký
   */
  clear: () => {
    sessionStorage.removeItem(REGISTRATION_DATA_KEY);
    // Cleanup legacy keys
    ["registrationData", "tempRegistrationData", "pendingRegistration"].forEach(
      (key) => sessionStorage.removeItem(key),
    );
  },

  /**
   * Lấy password an toàn (helper function cho OTP screen)
   */
  getPassword: (): string | undefined => {
    const data = registrationStorage.get();
    return data?.password;
  },
};
